/**
 * Selector-ownership helpers for the band swap: attribute selectors that name an
 * id/class, the tag set a band actually contains, and the page-context checks that
 * decide whether a rule with an outside ancestor/sibling really styles the band.
 * Pure text apart from `matchesContext`, which needs a DOM (jsdom, happy-dom, a browser).
 */

import { matchParen } from './cssScan';

// [id="x"] / [id=x] / [id~="x" i] / [class^="x"] ... — attr name limited to id|class.
const ATTR_ID_CLASS =
  /\[\s*(id|class)\s*([~^*$|]?=)\s*(?:"([^"]*)"|'([^']*)'|([^\]\s"'=]+))\s*([iIsS])?\s*\]/g;

export type AttrTokens = {
  /** values named with `=` or `~=` (an exact/whitespace-list match — treatable as a plain id token). */
  ids: string[];
  /** values named with `=` or `~=` on `class`. */
  classes: string[];
  /** `^= *= $= |=` operators on id/class — a substring match a swap cannot resolve token-for-token. */
  risky: Array<{ attr: 'id' | 'class'; op: string; val: string }>;
};

/** The id/class tokens a selector fragment names through `[attr]` selectors. */
export function attrSelectorTokens(text: string): AttrTokens {
  const ids: string[] = [];
  const classes: string[] = [];
  const risky: Array<{ attr: 'id' | 'class'; op: string; val: string }> = [];
  ATTR_ID_CLASS.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = ATTR_ID_CLASS.exec(text)) !== null) {
    const attr = m[1].toLowerCase() as 'id' | 'class';
    const op = m[2];
    const val = m[3] ?? m[4] ?? m[5] ?? '';
    if (op === '=' || op === '~=') (attr === 'id' ? ids : classes).push(val);
    else risky.push({ attr, op, val });
  }
  return { ids, classes, risky };
}

/** Could a `[attr op val]` selector match `token`? Conservative; used only to refuse risky ops that reach a band token. */
export function attrOpCouldMatch(op: string, val: string, token: string): boolean {
  switch (op) {
    case '^=':
      return val !== '' && token.startsWith(val);
    case '$=':
      return val !== '' && token.endsWith(val);
    case '*=':
      return val !== '' && token.includes(val);
    case '|=':
      return token === val || token.startsWith(`${val}-`);
    default:
      return false;
  }
}

/** Lowercased element tag names present in a markup fragment (a superset — comment/script tags included is harmless). */
export function elementTags(fragment: string): Set<string> {
  const out = new Set<string>();
  for (const m of fragment.matchAll(/<([a-zA-Z][\w-]*)/g)) out.add(m[1].toLowerCase());
  return out;
}

/**
 * The element tags Gravity Forms injects into a form slot when the page is published.
 * The STORED band has none of them — the writer emits a `[gravityform]` shortcode or a
 * `data-landing-form-slot` placeholder — so a rule aimed at one of these tags would be
 * dropped as "outside the band" though the published band DOES carry it. The list is a
 * superset of the shape X2Y2's `d3-styles/forms.mjs` injects (form/label/input/select/
 * option/textarea); `button`, `fieldset` and `legend` cover GF's button-mode submit and
 * grouped fields (radio/checkbox/section), and only ever fail closed (an unused tag adds
 * no matching rule on the carry side and can only over-refuse on the gate side).
 */
export const GF_INJECTED_TAGS = [
  'button', 'fieldset', 'form', 'input', 'label', 'legend', 'option', 'select', 'textarea',
] as const;

/**
 * A GF-shaped fragment carrying every {@link GF_INJECTED_TAGS} tag, dropped into a band's
 * form slot so a static relevance test sees the controls the published band will have.
 * Wrapper classes mirror real Gravity Forms output; the slot's own classes supply the
 * ancestor context for `.lp-form__embed input`-style rules.
 */
export const GF_FORM_MARKUP =
  '<div class="gform_wrapper gravity-theme"><form method="post"><div class="gform_body">' +
  '<ul class="gform_fields">' +
  '<li class="gfield"><label class="gfield_label"></label><div class="ginput_container"><input type="text"></div></li>' +
  '<li class="gfield"><label class="gfield_label"></label><div class="ginput_container"><select><option></option></select></div></li>' +
  '<li class="gfield"><label class="gfield_label"></label><div class="ginput_container"><textarea></textarea></div></li>' +
  '<li class="gfield"><fieldset><legend></legend><label></label><input type="radio"></fieldset></li>' +
  '</ul></div><div class="gform_footer"><input type="submit" class="gform_button button"><button type="button" class="gform_button"></button></div></form></div>';

/** A band holds a Gravity Forms slot: a `data-landing-form-slot` placeholder or a `[gravityform` shortcode. */
export function hasFormSlot(fragment: string): boolean {
  return /\bdata-landing-form-slot\b/i.test(fragment) || /\[gravityform\b/i.test(fragment);
}

/**
 * The tag set a band EFFECTIVELY contains: the tags literally in its markup, plus — when it
 * holds a Gravity Forms slot — the control tags GF injects at publish time. One shared list
 * for both sides: it decides which source rules travel (`compoundReach`) and, via the same
 * injected markup, which destination rules the typography gate must weigh.
 */
export function bandTagSet(fragment: string): Set<string> {
  const tags = elementTags(fragment);
  if (hasFormSlot(fragment)) for (const t of GF_INJECTED_TAGS) tags.add(t);
  return tags;
}

const DYNAMIC_PSEUDO =
  /:(?:hover|focus(?:-within|-visible)?|active|checked|target(?:-within)?|visited|link|enabled|disabled|indeterminate|placeholder-shown|autofill|user-(?:in)?valid|current|past|future|playing|paused|default|read-only|read-write|required|optional|in-range|out-of-range)\b/i;

/** Why a page-context selector cannot be verified with `matches()`, or null when it is safe to evaluate. */
export function unsafeContextReason(ctx: string): string | null {
  if (/:has\(/i.test(ctx)) return 'a :has() condition';
  if (DYNAMIC_PSEUDO.test(ctx)) return 'a state-dependent pseudo-class';
  return null;
}

/**
 * `ctx` with every `:has()` and state pseudo-class removed. That only widens the
 * selector, so when the widened form cannot match the band's root, no page state
 * can make `ctx` match it either.
 */
export function statelessContext(ctx: string): string {
  let out = '';
  let i = 0;
  while (i < ctx.length) {
    const rest = ctx.slice(i);
    if (/^:has\(/i.test(rest)) { i = matchParen(ctx, i + 4) + 1; continue; }
    const state = new RegExp(`^${DYNAMIC_PSEUDO.source}`, 'i').exec(rest);
    if (state) { i += state[0].length; continue; }
    out += ctx[i];
    i += 1;
  }
  return out;
}

// Pseudo-elements never match a real element in matches()/querySelector() (and `::x` throws),
// so they must be stripped before a relevance test; with statelessContext (which drops :has()
// and state pseudo-classes) this only WIDENS a selector, so a widened miss proves no page state
// could make it reach the band.
const PSEUDO_ELEMENT =
  /::[\w-]+(?:\([^)]*\))?|:(?:before|after|first-line|first-letter|placeholder|marker|selection|backdrop|file-selector-button)\b/gi;

/**
 * `sel` widened for a static `matches()`/`querySelector()` relevance test: no pseudo-elements,
 * no page state, and no `:not()`. Dropping `:not(…)` matters because a swap RENAMES the band's
 * private classes, so `a:not(.btn)` flips from excluding a `.btn` link (source) to including the
 * same link once it is `.fav-btn` (destination) — removing the negation keeps both sides symmetric,
 * and since it only ADDS matches it errs toward relevant (fail closed).
 */
export function widenForMatch(sel: string): string {
  const base = statelessContext(sel); // drops :has() and state pseudo-classes
  let out = '';
  let i = 0;
  while (i < base.length) {
    if (/^:not\(/i.test(base.slice(i))) { i = matchParen(base, i + 4) + 1; continue; }
    out += base[i];
    i += 1;
  }
  return out.replace(PSEUDO_ELEMENT, '').trim();
}

/** `root.matches(ctx)` with a fail-closed `'error'` when matches() throws on an unsupported selector. */
export function matchesContext(root: Element, ctx: string): boolean | 'error' {
  try {
    return root.matches(ctx);
  } catch {
    return 'error';
  }
}

/**
 * Drop GF-shaped controls into the band's form slot (or, absent a slot element, the root) so a
 * static relevance test sees the inputs/labels/etc. the published band will carry. A no-op on a
 * node that cannot take the markup, which only under-refuses.
 */
export function injectFormControls(root: Element): void {
  const slot = (root.matches('[data-landing-form-slot]') ? root : root.querySelector('[data-landing-form-slot]')) ?? root;
  try {
    slot.insertAdjacentHTML('beforeend', GF_FORM_MARKUP);
  } catch {
    /* detached / unusual node: relevance simply won't see controls */
  }
}

/**
 * Does `arm` reach the band? True when its widened form matches the band root or any element
 * inside it. A throw (an unsupported selector) or an empty widened form ⇒ relevant (fail closed:
 * widening only adds matches, so a real miss is a safe drop; anything unprovable is kept).
 */
export function armReachesBand(root: Element, arm: string): boolean {
  const w = widenForMatch(arm);
  if (!w) return true;
  try {
    return root.matches(w) || root.querySelector(w) !== null;
  } catch {
    return true;
  }
}

/** Split a recorded context `"<ancestor> <sep> *"` into the ancestor and its relation to the band, for refusal wording. */
export function describeContext(ctx: string): { ancestor: string; relation: 'under' | 'directly after' } {
  const sib = /^(.*?)\s*([+~])\s*\*$/.exec(ctx);
  if (sib) return { ancestor: sib[1].trim(), relation: 'directly after' };
  const desc = /^(.*?)\s*(?:>\s*)?\*$/.exec(ctx);
  return { ancestor: (desc?.[1] ?? ctx).trim(), relation: 'under' };
}
